import React, { useEffect, useState } from "react";
import { useHistory, Link } from "react-router-dom";
import { useJwt } from "react-jwt";
import logo from "../../img/katsina_state_logo.png";
import AxiosInstance from "../authorization/AxiosInstance";
import ProjectsAdmin from "./NewsAdmin";
import UsersAdmin from "./UsersAdmin";
import LeadershipAdmin from "./LeadershipAdmin";
import HeroAdmin from "./HeroAdmin";
import StatsAdmin from "./StatsAdmin";
import LeaderProfilesAdmin from "./LeaderProfilesAdmin";

const NAV = [
  { key: "overview", label: "Overview", icon: "▦" },
  { key: "hero", label: "Hero", icon: "🖼" },
  { key: "stats", label: "Stats Banner", icon: "📊" },
  { key: "news", label: "Projects", icon: "📰" },
  { key: "leadership", label: "Values / Vision / Mission", icon: "🎯" },
  { key: "leaderProfiles", label: "Leadership Section", icon: "👤" },
  { key: "users", label: "Users", icon: "👥" },
];

function StatCard({ label, value, color }) {
  return (
    <div className={`bg-white rounded-xl shadow p-5 flex flex-col gap-1 border-l-4 ${color}`}>
      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{label}</span>
      <span className="text-3xl font-bold text-gray-800">{value}</span>
    </div>
  );
}

function Overview() {
  const [stats, setStats] = useState({ news: "—", users: "—" });

  useEffect(() => {
    Promise.all([
      AxiosInstance.get("/news").catch(() => ({ data: [] })),
      AxiosInstance.get("/users").catch(() => ({ data: [] })),
    ]).then(([newsRes, usersRes]) => {
      setStats({
        news: newsRes.data.length,
        users: usersRes.data.length,
      });
    });
  }, []);

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-800 mb-6">Dashboard Overview</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        <StatCard label="Total Projects" value={stats.news} color="border-blue-500" />
        <StatCard label="Total Users" value={stats.users} color="border-purple-500" />
      </div>
      <div className="bg-white rounded-xl shadow p-5">
        <p className="text-sm text-gray-500">
          Use the sidebar to manage <strong>Projects</strong> and <strong>Users</strong>.
          Projects appear on the public landing page in order of the <em>Order</em> field (ascending).
        </p>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [activeNav, setActiveNav] = useState("overview");
  const history = useHistory();
  const token = localStorage.getItem("access_token");
  const { decodedToken } = useJwt(token);

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    history.push("/");
  };

  const content = {
    overview: <Overview />,
    hero: <HeroAdmin />,
    stats: <StatsAdmin />,
    news: <ProjectsAdmin />,
    leadership: <LeadershipAdmin />,
    leaderProfiles: <LeaderProfilesAdmin />,
    users: <UsersAdmin />,
  };

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      {/* Sidebar */}
      <aside className="w-56 flex-shrink-0 bg-white shadow-md flex flex-col">
        {/* Logo */}
        <div className="px-5 py-5 border-b">
          <Link to="/" className="flex items-center gap-2 no-underline">
            <img src={logo} alt="Katsina State" className="h-10 w-10 object-contain" />
            <div className="leading-tight">
              <div className="font-bold text-gray-800 text-sm">Admin</div>
              <div className="text-xs text-gray-400 mt-0.5">Backoffice Control</div>
            </div>
          </Link>
        </div>

        {/* Nav items */}
        <nav className="flex-1 py-4 px-3">
          {NAV.map((item) => (
            <button
              key={item.key}
              onClick={() => setActiveNav(item.key)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium mb-1 transition-colors ${
                activeNav === item.key
                  ? "bg-blue-50 text-blue-700"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              <span>{item.icon}</span>
              {item.label}
            </button>
          ))}
        </nav>

        {/* User + logout */}
        <div className="px-4 py-4 border-t">
          <div className="text-xs text-gray-400 mb-1 truncate">{decodedToken?.username}</div>
          <span className="inline-block text-xs bg-purple-100 text-purple-700 font-semibold px-2 py-0.5 rounded-full mb-3">
            {decodedToken?.role}
          </span>
          <button
            onClick={handleLogout}
            className="w-full text-left text-xs text-red-500 hover:text-red-700 font-medium"
          >
            Log out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="bg-white shadow-sm px-8 py-4 flex items-center justify-between flex-shrink-0">
          <h1 className="font-bold text-gray-700 text-lg">
            {NAV.find((n) => n.key === activeNav)?.label}
          </h1>
          <button
            onClick={() => history.push("/")}
            className="text-sm text-gray-500 hover:text-gray-800 font-medium"
          >
            ← Back to site
          </button>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto px-8 py-6">
          {content[activeNav]}
        </main>
      </div>
    </div>
  );
}
